import '../amplify-config';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import { REQUIRED_QUESTION_COUNT } from '../../shared/assessment';
import type { StudentDetails } from '../../shared/student-details';
import type { Locale } from '../i18n/copy';
import { fallbackQuestions } from './fallbackQuestions';

export type QuestionCategory = 'GRAMMAR' | 'VOCABULARY';
export type QuestionDifficulty = 'A1' | 'A2' | 'B1' | 'B2' | 'C1';

export interface PublicQuestion {
  id: string;
  order: number;
  prompt: string;
  options: string[];
  category: QuestionCategory;
  difficulty: QuestionDifficulty;
}

export interface TestAnswer {
  questionId: string;
  optionIndex: number;
}

export interface AssessmentResult {
  attemptId: string;
  score: number;
  total: number;
  level: string;
  teacherName: string;
  teacherPhone: string;
  teacherTelegram: string;
  bookingUrl: string;
}

interface QuestionResponse {
  questions: PublicQuestion[];
  isFallback: boolean;
}

interface QeaTestApi {
  getQuestions: () => Promise<PublicQuestion[]>;
  submit: (answers: TestAnswer[], locale: Locale) => Promise<AssessmentResult>;
  sendResult: (answers: TestAnswer[], locale: Locale, student: StudentDetails) => Promise<boolean>;
}

declare global {
  interface Window {
    __QEA_TEST_API__?: QeaTestApi;
  }
}

const client = generateClient<Schema>();

export async function getQuestions(): Promise<QuestionResponse> {
  if (window.__QEA_TEST_API__) {
    return { questions: await window.__QEA_TEST_API__.getQuestions(), isFallback: false };
  }

  try {
    const { data, errors } = await client.queries.getActiveQuestions();
    if (errors?.length || !data || data.length !== REQUIRED_QUESTION_COUNT) {
      return { questions: fallbackQuestions, isFallback: true };
    }

    const questions: PublicQuestion[] = data
      .filter((question): question is NonNullable<typeof question> => Boolean(question))
      .map((question) => ({
        id: question.id,
        order: question.order,
        prompt: question.prompt,
        options: question.options.filter((option): option is string => typeof option === 'string'),
        category: question.category as QuestionCategory,
        difficulty: question.difficulty as QuestionDifficulty,
      }));

    return { questions: questions.sort((a, b) => a.order - b.order), isFallback: false };
  } catch {
    return { questions: fallbackQuestions, isFallback: true };
  }
}

export async function submitAssessment(
  answers: TestAnswer[],
  locale: Locale,
): Promise<AssessmentResult> {
  if (window.__QEA_TEST_API__) return window.__QEA_TEST_API__.submit(answers, locale);

  const { data, errors } = await client.mutations.submitEnglishLevelTest({
    answers,
    locale,
  });
  if (errors?.length || !data) throw new Error('Assessment submission failed');

  return {
    attemptId: data.attemptId,
    score: data.score,
    total: data.total,
    level: data.level,
    teacherName: data.teacherName,
    teacherPhone: data.teacherPhone,
    teacherTelegram: data.teacherTelegram,
    bookingUrl: data.bookingUrl,
  };
}

export async function sendAssessmentResultToTeacher(
  answers: TestAnswer[],
  locale: Locale,
  student: StudentDetails,
): Promise<void> {
  if (window.__QEA_TEST_API__) {
    const wasSent = await window.__QEA_TEST_API__.sendResult(answers, locale, student);
    if (!wasSent) throw new Error('Assessment result notification failed');
    return;
  }

  const { data, errors } = await client.mutations.sendEnglishLevelTestResult({
    answers,
    locale,
    studentName: student.name,
    studentPhone: student.phone,
  });
  if (errors?.length || data !== true) throw new Error('Assessment result notification failed');
}
